/**
 * AoAConfigModal — edit the tenant's Agent of Agents persona + attached
 * knowledge + skills. Opens from the "Edit" button in the AoA page header
 * (gated on modules.agent_of_agents.edit_prompt). The edit applies tenant-wide.
 *
 * This deliberately reuses the regular Edit-Agent modal shell (BotModal.css)
 * so the AoA editor looks and feels identical to editing a normal agent:
 * a custom `.modal-overlay > .modal` with a header, `.modal-tabs`, scrollable
 * `.modal-tab-content`, and a `.modal-actions` footer.
 *
 * The persona textarea edits ONLY the editable persona half of the AoA's
 * instruction; the platform's tool/permission/safety machinery is separate and
 * always applies (see backend agent_of_agents_internals.md).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import BotOutputFormatsTab from '@modules/chatbots/components/BotOutputFormatsTab';
import type { OutputFormat } from '@/types/models';
import { useFeature } from '@hooks/useFeature';
import { DefaultModelPicker } from '@/components/common/DefaultModelPicker';
import api from '@services/api';
import { ExpandableTextarea } from '@/components/common';
import {
  aoaApi,
  type AoAConfig,
  type AoAConfigFile,
  type AoAConfigLibraryFolder,
} from '../services/aoaApi';
import { DataBundleApi } from '@modules/data-bundles/services/dataBundleService';
import type { DataBundle } from '@modules/data-bundles/types';
import { SkillsApi } from '@modules/skills/services/skillsApi';
import type { Skill } from '@modules/skills/types';
import { VaultPickerModal, type VaultPick } from '@modules/vault/components/VaultPickerModal';
// Reuse the regular Edit-Agent modal styles so the AoA editor matches it 1:1.
import '@modules/chatbots/components/BotModal.css';
import { SkillIcon, BundleIcon, LibraryFolderIcon } from '../entityIcons';
import './AoAConfigModal.css';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The organization's AoA model slot after a default-model change was saved,
   *  so the page shows it on the very next conversation without a reload. */
  onModelSaved?: (model: NonNullable<AoAConfig['model']>) => void;
}

type TabKey = 'persona' | 'knowledge' | 'skills' | 'files';

/** What a save sends for the persona and its knowledge. Built from the loaded
 *  config AND from the form by this one function, so a save can tell whether
 *  anything here changed — and send nothing when only, say, the default model
 *  did (an unrelated save used to re-send it and could flip an inheriting
 *  organization to its own copy of the default persona). */
function personaPayloadOf(v: {
  mode: PersonaMode; text: string; publishDown: boolean; fileIds: string[];
  bundleIds: string[]; skillIds: string[]; folderIds: string[] | null;
}) {
  return {
    // The custom text is ALWAYS persisted (even while another persona is
    // live) so a mode switch is never destructive. Empty custom text clears
    // the stored custom persona entirely.
    system_prompt: v.text.trim() ? v.text : null,
    use_default_persona: v.mode !== 'custom',
    persona_source: v.mode === 'inherit' ? 'inherit' as const : 'local' as const,
    // An inheriting tenant has nothing of its own to hand down; the backend
    // drops the flag too, but don't even claim it here.
    publish_to_descendants: v.mode === 'inherit' ? false : v.publishDown,
    attached_file_ids: v.fileIds,
    data_bundle_ids: v.bundleIds,
    skill_ids: v.skillIds,
    // Only the Library UI owns this list — a bundle-world save omits the key
    // so the backend leaves any stored folders untouched.
    ...(v.folderIds ? { library_folder_ids: v.folderIds } : {}),
  };
}

/** What a save sends for file capabilities — same contract as personaPayloadOf. */
function filesPayloadOf(v: { creation: boolean; editing: boolean; formats: string[] }) {
  return {
    file_creation_enabled: v.creation,
    file_editing_enabled: v.editing,
    // Only meaningful while creation is on; sending it when creation is off
    // would let an empty selection get rejected for no reason.
    ...(v.creation ? { file_formats: v.formats } : {}),
  };
}

/**
 * Where this tenant's AoA persona comes from.
 *  - `inherit` — follow the nearest ancestor that published one.
 *  - `default` — this tenant explicitly runs the platform default. A real
 *    choice, distinct from `inherit`: it overrides an ancestor's persona.
 *  - `custom`  — this tenant's own text.
 */
type PersonaMode = 'inherit' | 'default' | 'custom';

function formatBytes(n?: number | null): string {
  if (!n || n <= 0) return '';
  const mb = n / (1024 * 1024);
  if (mb >= 0.1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

async function downloadFileById(fileId: string, fileName: string): Promise<void> {
  const res = await api.get(`/files/download-by-id/${fileId}`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export const AoAConfigModal: React.FC<Props> = ({ open, onClose, onModelSaved }) => {
  const { t } = useLanguage();
  // Library tenants: the bundle surface is retired (the retired API 404s), so
  // the bundle section swaps for the Library folder picker (library_folder_ids).
  const libraryOn = useFeature('modules.library');

  // May THIS caller edit the persona half? The dialog can also be opened by an
  // admin whose tenant has files but not modules.agent_of_agents.edit_prompt -
  // for them the persona tabs are absent and Files is the whole dialog, because
  // showing a Save button that will 403 is worse than not showing the tab.
  const canEditPersona = useFeature('modules.agent_of_agents.edit_prompt');
  // The model the meta-agent starts a conversation on (a family key; null =
  // platform default). Same storage as the super-admin Meta-agents tab.
  const [defaultModel, setDefaultModel] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('persona');
  const [loading, setLoading] = useState(false);
  // File capabilities. Held apart from the persona/knowledge state because
  // they save through their own endpoint under their own permission - see
  // aoaApi.updateFileCapabilities.
  const [fileCreation, setFileCreation] = useState(true);
  const [fileEditing, setFileEditing] = useState(false);
  const [fileFormats, setFileFormats] = useState<string[]>([]);
  // Web search — a separate group-admin toggle, OFF by default, no master flag.
  const [webSearch, setWebSearch] = useState(false);
  const [driveEditing, setDriveEditing] = useState(false);
  const [driveEditingTenant, setDriveEditingTenant] = useState(false);
  // The shared Artifacts tab speaks {format: on}; the API stores the list of
  // what is ON. Bridge here so neither side has to know about the other.
  const formatMap = useMemo(
    () => Object.fromEntries(fileFormats.map((f) => [f, true])) as Partial<
      Record<OutputFormat, boolean>
    >,
    [fileFormats],
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [config, setConfig] = useState<AoAConfig | null>(null);
  const [personaText, setPersonaText] = useState('');
  // Which persona RUNS: the one published by a parent tenant, the platform
  // default, or this tenant's custom text. Switching modes never deletes the
  // custom text — it stays saved.
  const [personaMode, setPersonaMode] = useState<PersonaMode>('default');
  // Hand this tenant's persona down to every tenant beneath it.
  const [publishDown, setPublishDown] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AoAConfigFile[]>([]);

  // Data bundles
  const [bundles, setBundles] = useState<DataBundle[]>([]);
  const [selectedBundleIds, setSelectedBundleIds] = useState<string[]>([]);
  const [showBundleSelector, setShowBundleSelector] = useState(false);
  const [bundleSearchQuery, setBundleSearchQuery] = useState('');

  // Library folders (modules.library tenants — replaces the bundle section).
  // Full objects, not just ids, so the cards render without a lookup list.
  const [selectedFolders, setSelectedFolders] = useState<AoAConfigLibraryFolder[]>([]);
  const [showFolderPicker, setShowFolderPicker] = useState(false);

  // Skills
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [skillsLoaded, setSkillsLoaded] = useState(false);
  const [showSkillSelector, setShowSkillSelector] = useState(false);
  const [skillSearchQuery, setSkillSearchQuery] = useState('');

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // The persona and file-capability payloads as loaded — a save sends each
  // only when the form now builds a different one.
  const savedRef = React.useRef<{ persona: string; files: string } | null>(null);

  // ---- Load config + option lists on open ----
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, bundleRes] = await Promise.all([
        aoaApi.getConfig(),
        // Library tenants: the bundle listing API is retired — don't call it.
        libraryOn
          ? Promise.resolve({ data_bundles: [] as DataBundle[], total: 0 })
          : DataBundleApi.listAvailableBundles().catch(
              () => ({ data_bundles: [] as DataBundle[], total: 0 }),
            ),
      ]);
      setConfig(cfg);
      // The CUSTOM text persists even while another persona is live, so the
      // admin can flip between modes freely. `personaText` holds ONLY the
      // custom text; the other modes render read-only previews.
      // 'inherit' is only a SELECTABLE mode when an ancestor is actually
      // publishing — the Inherit button renders behind `inheritable_from`.
      // The backend defaults a never-configured tenant to persona_source
      // 'inherit' (legacy fallback in _persona_source), and a root tenant has
      // no ancestor to inherit from, so this modal opened with personaMode
      // 'inherit' and NO button to light up: Default and Custom both looked
      // unpressed. With nobody publishing the backend already runs the
      // platform default, so that is the honest mode to show.
      // Inheriting with a publisher → 'inherit'. Inheriting with nobody
      // publishing → the resolver runs the platform default, so say
      // 'default'. Never 'custom' here: stored text is dormant while the
      // tenant is in inherit mode, and showing 'custom' would claim the
      // agent is running writing that it isn't.
      const mode: PersonaMode = cfg.persona_source === 'inherit'
        ? (cfg.inheritable_from ? 'inherit' : 'default')
        : (cfg.system_prompt && !cfg.use_default_persona ? 'custom' : 'default');
      const loaded = {
        mode, text: cfg.system_prompt ?? '', publishDown: cfg.publish_to_descendants ?? false,
        files: cfg.attached_files || [],
        bundleIds: (cfg.data_bundles || []).map((b) => b.data_bundle_id),
        folders: cfg.library_folders || [],
        skillIds: (cfg.skills || []).map((s) => s.skill_id),
        creation: cfg.file_capabilities?.can_create ?? false,
        editing: cfg.file_capabilities?.can_edit_files ?? false,
        formats: cfg.file_capabilities?.formats ?? [],
      };
      setPersonaMode(loaded.mode);
      setPublishDown(loaded.publishDown);
      setPersonaText(loaded.text);
      setAttachedFiles(loaded.files);
      setSelectedBundleIds(loaded.bundleIds);
      setSelectedFolders(loaded.folders);
      setSelectedSkillIds(loaded.skillIds);
      setFileCreation(loaded.creation);
      setFileEditing(loaded.editing);
      setFileFormats(loaded.formats);
      savedRef.current = {
        persona: JSON.stringify(personaPayloadOf({
          mode: loaded.mode, text: loaded.text, publishDown: loaded.publishDown,
          fileIds: loaded.files.map((f) => f.file_id), bundleIds: loaded.bundleIds,
          skillIds: loaded.skillIds,
          folderIds: libraryOn ? loaded.folders.map((f) => f.folder_id) : null,
        })),
        files: JSON.stringify(filesPayloadOf(loaded)),
      };
      setWebSearch(cfg.web_search?.enabled ?? false);
      setDriveEditing(cfg.drive_editing?.enabled ?? false);
      setDefaultModel(cfg.model && !cfg.model.is_default ? cfg.model.default_model : null);
      setDriveEditingTenant(cfg.drive_editing?.tenant_enabled ?? false);
      setBundles(bundleRes.data_bundles || []);
    } catch {
      message.error(t('metaAgent.config.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, libraryOn]);

  useEffect(() => {
    if (open) {
      // A files-only caller lands on the one tab that is theirs.
      setActiveTab(canEditPersona ? 'persona' : 'files');
      setSkillsLoaded(false);
      setAvailableSkills([]);
      load();
    }
  }, [open, load, canEditPersona]);

  // Lazy-load skills the first time the Skills tab is opened.
  const loadSkills = useCallback(async () => {
    setLoadingSkills(true);
    try {
      const res = await SkillsApi.listAvailableSkills({});
      setAvailableSkills(res.skills || []);
    } catch {
      setAvailableSkills([]);
    } finally {
      setLoadingSkills(false);
      setSkillsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (open && activeTab === 'skills' && !skillsLoaded) {
      loadSkills();
    }
  }, [open, activeTab, skillsLoaded, loadSkills]);

  // ---- Files ----
  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFilesChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      let cfg: AoAConfig | null = null;
      for (const file of files) {
        cfg = await aoaApi.uploadConfigFile(file);
      }
      if (cfg) {
        setConfig(cfg);
        setAttachedFiles(cfg.attached_files || []);
      }
      message.success(t('metaAgent.config.fileAdded'));
    } catch (err: any) {
      message.error(err?.response?.data?.error || t('metaAgent.config.fileError'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = async (fileId: string) => {
    try {
      const cfg = await aoaApi.deleteConfigFile(fileId);
      setConfig(cfg);
      setAttachedFiles(cfg.attached_files || []);
    } catch {
      message.error(t('metaAgent.config.fileRemoveError'));
    }
  };

  // ---- "Direct to context" toggle — same concept as the regular
  // Edit-Agent page's per-attachment include_as_context switch. ----
  const CONTEXT_ALLOWED_EXTENSIONS = ['.txt', '.md', '.csv', '.json', '.doc', '.docx', '.pdf', '.pages'];
  const isContextAllowed = (fileType: string, fileName: string): boolean => {
    const ext = '.' + (fileName || '').split('.').pop()?.toLowerCase();
    const mime = (fileType || '').toLowerCase();
    if (mime.startsWith('image/') || mime.startsWith('video/') || mime.startsWith('audio/')) return false;
    return CONTEXT_ALLOWED_EXTENSIONS.includes(ext);
  };
  const [togglingFileIds, setTogglingFileIds] = useState<Set<string>>(() => new Set());
  const toggleFileContext = async (f: AoAConfigFile) => {
    const next = !f.include_as_context;
    setTogglingFileIds((prev) => new Set(prev).add(f.file_id));
    try {
      const cfg = await aoaApi.setConfigFileContext(f.file_id, next);
      setConfig(cfg);
      setAttachedFiles(cfg.attached_files || []);
      message.success(t('bots.contextFiles.contextToggleSuccess'));
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('bots.contextFiles.contextToggleError');
      message.error(msg);
    } finally {
      setTogglingFileIds((prev) => {
        const s = new Set(prev);
        s.delete(f.file_id);
        return s;
      });
    }
  };

  // ---- Bundles ----
  const selectedBundles = useMemo(
    () => bundles.filter((b) => selectedBundleIds.includes(b.data_bundle_id)),
    [bundles, selectedBundleIds],
  );

  const filteredBundles = useMemo(() => {
    // Only home-tenant bundles are linkable to the AoA (the backend drops
    // cross-tenant / shared ids), so keep shared ones out of the picker.
    const linkable = bundles.filter((b) => !b.is_shared);
    if (!bundleSearchQuery.trim()) return linkable;
    const q = bundleSearchQuery.toLowerCase();
    return linkable.filter(
      (b) =>
        b.bundle_name.toLowerCase().includes(q) ||
        (b.bundle_description || '').toLowerCase().includes(q) ||
        (b.created_by_name || '').toLowerCase().includes(q),
    );
  }, [bundles, bundleSearchQuery]);

  const toggleBundleSelection = (bundleId: string) => {
    setSelectedBundleIds((prev) =>
      prev.includes(bundleId) ? prev.filter((id) => id !== bundleId) : [...prev, bundleId],
    );
  };

  // ---- Library folders ----
  const removeFolder = (folderId: string) => {
    setSelectedFolders((prev) => prev.filter((f) => f.folder_id !== folderId));
  };

  const handleFolderPick = (pick: VaultPick) => {
    // The picker runs foldersOnly, so a file ref can't arrive — guard anyway.
    if (!pick.folder_id) return;
    setSelectedFolders((prev) =>
      prev.some((f) => f.folder_id === pick.folder_id)
        ? prev
        : [...prev, { folder_id: pick.folder_id!, folder_name: pick.name }],
    );
    setShowFolderPicker(false);
  };

  // ---- Skills ----
  const filteredSkills = useMemo(() => {
    // Only home-tenant skills are linkable to the AoA (the backend drops
    // cross-tenant / shared ids), so keep shared ones out of the picker.
    const active = availableSkills.filter((s) => s.is_active && !s.is_shared);
    if (!skillSearchQuery.trim()) return active;
    const q = skillSearchQuery.toLowerCase();
    return active.filter(
      (s) =>
        s.skill_name.toLowerCase().includes(q) ||
        (s.skill_description || '').toLowerCase().includes(q),
    );
  }, [availableSkills, skillSearchQuery]);

  const toggleSkill = (skillId: string) => {
    setSelectedSkillIds((prev) =>
      prev.includes(skillId) ? prev.filter((id) => id !== skillId) : [...prev, skillId],
    );
  };

  const removeSkill = (skillId: string) => {
    setSelectedSkillIds((prev) => prev.filter((id) => id !== skillId));
  };

  // Assigned-skill display cards. When a selected skill hasn't loaded into
  // availableSkills yet (e.g. Skills tab never opened, but hydrated from
  // config), fall back to the config's name so the card still renders.
  const assignedSkillCards = useMemo(() => {
    const byId = new Map(availableSkills.map((s) => [s.skill_id, s]));
    return selectedSkillIds.map((id) => {
      const full = byId.get(id);
      const fromConfig = (config?.skills || []).find((s) => s.skill_id === id);
      return {
        skill_id: id,
        skill_name: full?.skill_name || fromConfig?.skill_name || id,
        skill_description: full?.skill_description || '',
      };
    });
  }, [selectedSkillIds, availableSkills, config]);

  // ---- Save ----
  const handleSave = async () => {
    setSaving(true);
    try {
      // Each part is sent only when it changed (personaPayloadOf).
      const persona = personaPayloadOf({
        mode: personaMode, text: personaText, publishDown,
        fileIds: attachedFiles.map((f) => f.file_id), bundleIds: selectedBundleIds,
        skillIds: selectedSkillIds,
        folderIds: libraryOn ? selectedFolders.map((f) => f.folder_id) : null,
      });
      if (canEditPersona && JSON.stringify(persona) !== savedRef.current?.persona) {
        await aoaApi.updateConfig(persona);
      }
      // Files save through their own endpoint, and only when the tenant
      // actually has the capability - posting to it otherwise would 403 and
      // fail a save that had nothing to do with files.
      const files = filesPayloadOf({ creation: fileCreation, editing: fileEditing, formats: fileFormats });
      if (config?.file_capabilities?.enabled && JSON.stringify(files) !== savedRef.current?.files) {
        await aoaApi.updateFileCapabilities(files);
      }
      // Web search rides its own endpoint (no master flag) and only when the
      // value actually changed, so an unrelated save never touches it.
      if (webSearch !== (config?.web_search?.enabled ?? false)) {
        await aoaApi.updateWebSearch(webSearch);
      }
      // Same contract for Drive editing: its own endpoint, touched only when
      // the value actually changed.
      if (driveEditing !== (config?.drive_editing?.enabled ?? false)) {
        await aoaApi.updateDriveEditing(driveEditing);
      }
      // The default model rides its own endpoint too (the super-admin tab's
      // key), only when changed, and only for someone who may edit the persona.
      const storedDefault = config?.model && !config.model.is_default ? config.model.default_model : null;
      if (canEditPersona && defaultModel !== storedDefault) {
        onModelSaved?.(await aoaApi.updateDefaultModel(defaultModel));
      }
      message.success(t('metaAgent.config.saved'));
      onClose();
    } catch {
      message.error(t('metaAgent.config.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const usingDefault = personaMode === 'default';
  const inheritable = config?.inheritable_from ?? null;
  const reach = config?.descendant_reach ?? { following: 0, overriding: 0 };
  const hasDescendants = reach.following + reach.overriding > 0;
  // The persona a parent published, ready to preview. A publishing ancestor
  // with no custom text standardised its subtree on the platform default.
  const inheritedPreview = inheritable
    ? (inheritable.system_prompt ?? config?.default_system_prompt ?? '')
    : '';

  const switchPersonaMode = (mode: PersonaMode) => {
    setPersonaMode(mode);
    // First switch to custom with nothing saved yet: seed the editor from
    // whatever is live today — the inherited persona if there is one,
    // otherwise the platform default — so the admin starts from a working
    // persona rather than a blank box.
    if (mode === 'custom' && !personaText.trim()) {
      setPersonaText(inheritedPreview || config?.default_system_prompt || '');
    }
  };

  const footer = (
    <div className="modal-actions">
      <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>
        {t('common.cancel')}
      </button>
      <button type="button" className="btn-primary" onClick={handleSave} disabled={saving || loading}>
        {saving ? (
          <span className="loading-indicator">
            <div className="loading-spinner" />
            <span>{t('common.updating')}</span>
          </span>
        ) : (
          <span>{t('common.save')}</span>
        )}
      </button>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <h2 className="modal-title">{t('metaAgent.config.title')}</h2>
            <p className="modal-subtitle">{t('metaAgent.config.personaHint')}</p>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs. The persona half needs edit_prompt; without it those three
            tabs are absent rather than present-but-broken, and Files is the
            whole dialog. */}
        <div className="modal-tabs">
          {canEditPersona && (
            <>
              <button
                type="button"
                className={`modal-tab ${activeTab === 'persona' ? 'active' : ''}`}
                onClick={() => setActiveTab('persona')}
              >
                {t('metaAgent.config.tabPersona')}
              </button>
              <button
                type="button"
                className={`modal-tab ${activeTab === 'knowledge' ? 'active' : ''}`}
                onClick={() => setActiveTab('knowledge')}
              >
                {t('metaAgent.config.tabKnowledge')}
              </button>
              <button
                type="button"
                className={`modal-tab ${activeTab === 'skills' ? 'active' : ''}`}
                onClick={() => setActiveTab('skills')}
              >
                {t('metaAgent.config.tabSkills')}
              </button>
            </>
          )}
          {/* Always shown: web search lives here and has no master flag, so a
              group admin can always reach it. The file-capability switches
              inside are themselves gated on the file master flag — a switch for
              something the tenant cannot do is a promise nobody can keep. */}
          <button
            type="button"
            className={`modal-tab ${activeTab === 'files' ? 'active' : ''}`}
            onClick={() => setActiveTab('files')}
          >
            {t('metaAgent.config.tabFilesAndWeb')}
          </button>
        </div>

        {loading || !config ? (
          <div className="modal-tab-content">
            <div className="loading-state">
              <div className="loading-spinner" />
              <p>{t('common.loading')}</p>
            </div>
            {footer}
          </div>
        ) : (
          <>
            {/* Persona tab */}
            {activeTab === 'persona' && (
              <div className="modal-tab-content">
                {/* Default model — the family the meta-agent starts a
                    conversation on. One storage with the super-admin
                    Meta-agents tab; the allowed list stays the super admin's. */}
                <div className="form-group" data-testid="aoa-default-model">
                  <label className="form-label">{t('models.defaultModel.label')}</label>
                  <DefaultModelPicker
                    value={defaultModel}
                    onChange={setDefaultModel}
                    allowedModels={config?.model?.allowed_models?.length ? config.model.allowed_models : 'all'}
                    disabled={saving}
                    unsetHint={t('models.defaultModel.unsetPlatform')}
                    help={t('metaAgent.config.defaultModelHint')}
                    testId="aoa-default-model-picker"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('metaAgent.config.personaLabel')}</label>
                  <p className="form-help-text aoa-persona-help">
                    {t('metaAgent.config.personaHint')}
                  </p>
                  {/* Which persona runs — one published by a parent tenant,
                      the platform default, or this tenant's custom text.
                      Switching NEVER deletes the custom text; it stays saved
                      for switching back. */}
                  <div className="aoa-persona-modes" role="tablist">
                    {inheritable && (
                      <button
                        type="button"
                        className={`aoa-persona-mode-btn${personaMode === 'inherit' ? ' aoa-persona-mode-btn--active' : ''}`}
                        onClick={() => switchPersonaMode('inherit')}
                        aria-pressed={personaMode === 'inherit'}
                        data-testid="aoa-persona-mode-inherit"
                      >
                        {t('metaAgent.config.modeInherit')}
                      </button>
                    )}
                    <button
                      type="button"
                      className={`aoa-persona-mode-btn${usingDefault ? ' aoa-persona-mode-btn--active' : ''}`}
                      onClick={() => switchPersonaMode('default')}
                      aria-pressed={usingDefault}
                      data-testid="aoa-persona-mode-default"
                    >
                      {t('metaAgent.config.modeDefault')}
                    </button>
                    <button
                      type="button"
                      className={`aoa-persona-mode-btn${personaMode === 'custom' ? ' aoa-persona-mode-btn--active' : ''}`}
                      onClick={() => switchPersonaMode('custom')}
                      aria-pressed={personaMode === 'custom'}
                      data-testid="aoa-persona-mode-custom"
                    >
                      {t('metaAgent.config.modeCustom')}
                    </button>
                  </div>

                  {/* Where the running persona comes from, named explicitly so
                      an admin is never confused about why the text below isn't
                      what they wrote. */}
                  {config.persona_origin === 'inherited' && personaMode === 'inherit' && (
                    <div className="aoa-inherit-banner" data-testid="aoa-inherit-banner">
                      {t('metaAgent.config.inheritedFrom', {
                        tenant: config.persona_origin_tenant_name ?? '',
                      })}
                    </div>
                  )}
                  {inheritable && personaMode !== 'inherit' && (
                    <div className="aoa-inherit-banner aoa-inherit-banner--muted">
                      {t('metaAgent.config.overridingAncestor', {
                        tenant: inheritable.tenant_name,
                      })}
                    </div>
                  )}

                  {personaMode === 'inherit' ? (
                    <>
                      <ExpandableTextarea
                        value={inheritedPreview}
                        onChange={() => undefined}
                        rows={10}
                        label={t('metaAgent.config.personaLabel')}
                        disabled
                      />
                      <div className="aoa-persona-actions">
                        <span className="aoa-persona-status">
                          {inheritable?.system_prompt
                            ? t('metaAgent.config.usingInherited', {
                                tenant: inheritable.tenant_name,
                              })
                            : t('metaAgent.config.usingInheritedDefault', {
                                tenant: inheritable?.tenant_name ?? '',
                              })}
                          {personaText.trim() ? ` ${t('metaAgent.config.customSaved')}` : ''}
                        </span>
                      </div>
                    </>
                  ) : usingDefault ? (
                    <>
                      <ExpandableTextarea
                        value={config.default_system_prompt || ''}
                        onChange={() => undefined}
                        rows={10}
                        label={t('metaAgent.config.personaLabel')}
                        disabled
                      />
                      <div className="aoa-persona-actions">
                        <span className="aoa-persona-status">
                          {t('metaAgent.config.usingDefault')}
                          {personaText.trim()
                            ? ` ${t('metaAgent.config.customSaved')}`
                            : ''}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <ExpandableTextarea
                        value={personaText}
                        onChange={setPersonaText}
                        placeholder={config.default_system_prompt}
                        rows={10}
                        label={t('metaAgent.config.personaLabel')}
                      />
                      <div className="aoa-persona-actions">
                        <span className="aoa-persona-status">
                          {t('metaAgent.config.usingCustom')}
                        </span>
                        <div className="aoa-persona-buttons">
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setPersonaText(config.default_system_prompt)}
                            disabled={personaText === (config.default_system_prompt ?? '')}
                          >
                            {t('metaAgent.config.resetToDefault')}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Hand this persona down the tenant tree. Only meaningful
                    when this tenant decides for itself and actually has
                    tenants beneath it. */}
                {hasDescendants && personaMode !== 'inherit' && (
                  <div className="form-group aoa-publish-group">
                    <div className="file-context-toggle aoa-publish-toggle">
                      <span className="context-toggle-label">
                        {t('metaAgent.config.publishLabel')}
                      </span>
                      <label className="context-switch">
                        <input
                          type="checkbox"
                          checked={publishDown}
                          onChange={() => setPublishDown((v) => !v)}
                          data-testid="aoa-publish-toggle"
                        />
                        <span className="context-switch-slider"></span>
                      </label>
                    </div>
                    <p className="form-help-text">{t('metaAgent.config.publishHint')}</p>
                    <p className="form-help-text aoa-publish-reach">
                      {t('metaAgent.config.publishReach', {
                        following: reach.following,
                        overriding: reach.overriding,
                      })}
                    </p>
                  </div>
                )}
                {footer}
              </div>
            )}

            {/* Knowledge tab */}
            {activeTab === 'knowledge' && (
              <div className="modal-tab-content">
                {/* Data Bundles — hidden on Library tenants (the bundle
                    surface is retired there; the Library folder picker
                    replaces this in the WS3 config work). */}
                {!libraryOn && (
                <div className="data-bundles-section aoa-bundles-section">
                  <label className="section-label">{t('metaAgent.config.bundlesLabel')}</label>
                  <p className="section-description">{t('metaAgent.config.bundlesHint')}</p>

                  {selectedBundles.length > 0 && (
                    <div className="bundle-selected-list">
                      {selectedBundles.map((bundle) => (
                        <div key={bundle.data_bundle_id} className="bundle-selected-card">
                          <div className="bundle-selected-icon">
                            <BundleIcon />
                          </div>
                          <div className="bundle-selected-info">
                            <div className="bundle-selected-name" dir="auto">{bundle.bundle_name}</div>
                            <div className="bundle-selected-meta">
                              {t('metaAgent.config.bundleFileCount', { count: bundle.file_count })}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="bundle-remove-btn"
                            onClick={() => toggleBundleSelection(bundle.data_bundle_id)}
                            title={t('common.remove')}
                            aria-label={t('common.remove')}
                          >
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn-add-bundle"
                    onClick={() => setShowBundleSelector(true)}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    {t('metaAgent.config.selectBundles')}
                  </button>
                </div>
                )}

                {/* Library folders — the Library-native replacement for the
                    bundle section. Attach whole folders; new files dropped
                    into them later reach the meta-agent automatically. */}
                {libraryOn && (
                <div className="data-bundles-section aoa-bundles-section">
                  <label className="section-label">{t('metaAgent.config.foldersLabel')}</label>
                  <p className="section-description">{t('metaAgent.config.foldersHint')}</p>

                  {selectedFolders.length > 0 && (
                    <div className="bundle-selected-list">
                      {selectedFolders.map((folder) => (
                        <div key={folder.folder_id} className="bundle-selected-card" data-testid="aoa-config-folder-card">
                          <div className="bundle-selected-icon">
                            <LibraryFolderIcon />
                          </div>
                          <div className="bundle-selected-info">
                            <div className="bundle-selected-name" dir="auto">{folder.folder_name}</div>
                            {typeof folder.file_count === 'number' && (
                              <div className="bundle-selected-meta">
                                {t('metaAgent.config.bundleFileCount', { count: folder.file_count })}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            className="bundle-remove-btn"
                            onClick={() => removeFolder(folder.folder_id)}
                            title={t('common.remove')}
                            aria-label={t('common.remove')}
                          >
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn-add-bundle"
                    onClick={() => setShowFolderPicker(true)}
                    data-testid="aoa-config-add-folder"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    {t('vault.attach.add')}
                  </button>
                </div>
                )}

                {/* Files */}
                <div className="skills-section">
                  <div className="section-header">
                    <div>
                      <label className="section-label">{t('metaAgent.config.filesLabel')}</label>
                      <p className="section-description">{t('metaAgent.config.filesHint')}</p>
                    </div>
                    <button
                      type="button"
                      className="btn-add-skill"
                      onClick={handleUploadClick}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <span className="loading-indicator">
                          <div className="loading-spinner" />
                        </span>
                      ) : (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      )}
                      {t('metaAgent.config.uploadFile')}
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFilesChosen}
                  />

                  {attachedFiles.length === 0 ? (
                    <div className="empty-skills">
                      <div className="empty-text">{t('metaAgent.config.noFiles')}</div>
                    </div>
                  ) : (
                    <div className="skills-list">
                      {attachedFiles.map((f) => (
                        <div key={f.file_id} className="aoa-file-card">
                          <div className="aoa-file-icon">
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                              <polyline points="13 2 13 9 20 9" />
                            </svg>
                          </div>
                          <div className="aoa-file-info">
                            <div className="aoa-file-name" dir="auto">{f.file_name}</div>
                            {formatBytes(f.file_size_bytes) && (
                              <div className="aoa-file-size">{formatBytes(f.file_size_bytes)}</div>
                            )}
                          </div>
                          {/* "Direct to context" — same switch the regular
                              Edit-Agent page has per attachment. Flagged files
                              are inlined into the AoA's instruction; others
                              stay searchable via its file tools. */}
                          <div
                            className="file-context-toggle"
                            title={
                              isContextAllowed(f.file_type || '', f.file_name || '')
                                ? t('bots.contextFiles.alwaysInContextTooltip')
                                : t('bots.contextFiles.contextToggleUnsupportedType')
                            }
                          >
                            <span className="context-toggle-label">
                              {t('bots.contextFiles.alwaysInContext')}
                            </span>
                            <label className="context-switch">
                              <input
                                type="checkbox"
                                checked={f.include_as_context || false}
                                onChange={() => toggleFileContext(f)}
                                disabled={
                                  !isContextAllowed(f.file_type || '', f.file_name || '') ||
                                  togglingFileIds.has(f.file_id)
                                }
                                data-testid="aoa-file-context-toggle"
                              />
                              <span className="context-switch-slider"></span>
                            </label>
                          </div>
                          <button
                            type="button"
                            className="btn-skill-info"
                            onClick={() => downloadFileById(f.file_id, f.file_name)}
                            title={t('common.download')}
                            aria-label={t('common.download')}
                          >
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="btn-remove-skill"
                            onClick={() => removeFile(f.file_id)}
                            title={t('common.remove')}
                            aria-label={t('common.remove')}
                          >
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {footer}
              </div>
            )}

            {/* Skills tab */}
            {activeTab === 'skills' && (
              <div className="modal-tab-content skills-tab">
                <div className="skills-section">
                  <div className="section-header">
                    <div>
                      <label className="section-label">{t('metaAgent.config.skillsLabel')}</label>
                      <p className="section-description">{t('metaAgent.config.skillsHint')}</p>
                    </div>
                    <button
                      type="button"
                      className="btn-add-skill"
                      onClick={() => setShowSkillSelector(true)}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      {t('metaAgent.config.addSkill')}
                    </button>
                  </div>

                  <div className="skills-list">
                    {selectedSkillIds.length === 0 ? (
                      <div className="empty-skills">
                        <div className="empty-text">{t('metaAgent.config.noSkills')}</div>
                        <div className="empty-subtext">{t('metaAgent.config.skillsHint')}</div>
                      </div>
                    ) : (
                      assignedSkillCards.map((skill) => (
                        <div key={skill.skill_id} className="skill-card">
                          <div className="skill-card-header aoa-skill-card-header">
                            <div className="skill-info">
                              <div className="skill-icon">
                                <SkillIcon />
                              </div>
                              <div className="skill-details">
                                <div className="skill-name-row">
                                  <span className="skill-name" dir="auto">{skill.skill_name}</span>
                                </div>
                                {skill.skill_description && (
                                  <div className="skill-description" dir="auto">{skill.skill_description}</div>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn-remove-skill"
                              onClick={() => removeSkill(skill.skill_id)}
                              title={t('common.remove')}
                              aria-label={t('common.remove')}
                            >
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {footer}
              </div>
            )}

            {/* Files tab - what the meta-agent may do with documents. Rendered
                only while the organization has the capability at all; the tab
                button above is gated on the same flag. */}
            {activeTab === 'files' && (
              <div className="modal-tab-content">
                {/* Web search — always available (no master flag). A group
                    admin can turn the meta-agent's live web lookup on or off. */}
                <div className="form-group">
                  <div className="file-context-toggle">
                    <span className="context-toggle-label">
                      {t('metaAgent.config.webSearchLabel')}
                    </span>
                    <label className="context-switch">
                      <input
                        type="checkbox"
                        checked={webSearch}
                        onChange={() => setWebSearch((v) => !v)}
                        data-testid="aoa-web-search-toggle"
                      />
                      <span className="context-switch-slider"></span>
                    </label>
                  </div>
                  <p className="form-help-text">{t('metaAgent.config.webSearchHint')}</p>
                </div>

                {/* Drive in-place editing — the meta-agent's level-2 Drive
                    switch. Level 1 (read / save) needs no switch here; this
                    one lets it rewrite existing files.

                    Shown only where the organisation was granted the
                    capability (tenant flag integrations.cloud_storage.editing,
                    super-admin → White-Label → Connectors). Nobody on this
                    screen can grant it, so without it the switch was a dead
                    control describing a power they could not have. That
                    tenant flag is the only ceiling (operator decision
                    2026-09-25): where the row shows, the switch works. */}
                {driveEditingTenant && (
                <div className="form-group">
                  <div className="file-context-toggle">
                    <span className="context-toggle-label">
                      {t('metaAgent.config.driveEditingLabel')}
                    </span>
                    <label className="context-switch">
                      <input
                        type="checkbox"
                        checked={driveEditing}
                        onChange={() => setDriveEditing((v) => !v)}
                        data-testid="aoa-drive-editing-toggle"
                      />
                      <span className="context-switch-slider"></span>
                    </label>
                  </div>
                  <p className="form-help-text">
                    {t('metaAgent.config.driveEditingHint')}
                  </p>
                </div>
                )}

                {/* File-authoring switches ride the super-admin master flag —
                    shown only when the tenant actually has the capability. */}
                {config.file_capabilities?.enabled && (<>
                <div className="form-group">
                  <div className="file-context-toggle">
                    <span className="context-toggle-label">
                      {t('metaAgent.config.fileCreationLabel')}
                    </span>
                    <label className="context-switch">
                      <input
                        type="checkbox"
                        checked={fileCreation}
                        onChange={() => setFileCreation((v) => !v)}
                        data-testid="aoa-file-creation-toggle"
                      />
                      <span className="context-switch-slider"></span>
                    </label>
                  </div>
                  <p className="form-help-text">{t('metaAgent.config.fileCreationHint')}</p>
                </div>

                {/* Which formats. Only while creation is on - offering a format
                    list for a capability that is off is a control that does
                    nothing. This is the SAME component the Edit-Agent dialog
                    uses for its Artifacts tab, over the narrower list the
                    meta-agent may produce, so the two read as one product. */}
                {fileCreation && (
                  <BotOutputFormatsTab
                    formats={config.file_capabilities.formats_available as OutputFormat[]}
                    value={formatMap}
                    onChange={(next) =>
                      setFileFormats(
                        (config.file_capabilities?.formats_available ?? []).filter(
                          (f) => next[f as OutputFormat],
                        ),
                      )
                    }
                    minEnabled={1}
                    title={t('metaAgent.config.fileFormatsLabel')}
                    description={t('metaAgent.config.fileFormatsHint')}
                  />
                )}

                {/* The "Edit files in the Library" switch is gone (operator
                    decision 2026-09-07). Library editing rides modules.library
                    alone: the user's own Library permissions are the gate, and
                    every change is still an approval card. */}
                </>)}

                {footer}
              </div>
            )}
          </>
        )}
      </div>

      {/* Library folder picker (foldersOnly: whole folders attach to the AoA;
          there is no per-link context flag here). */}
      <VaultPickerModal
        open={showFolderPicker}
        foldersOnly
        attached={new Set(selectedFolders.map((f) => f.folder_id))}
        onPick={handleFolderPick}
        onCancel={() => setShowFolderPicker(false)}
      />

      {/* Bundle Selector Modal */}
      {showBundleSelector && (
        <div className="modal-overlay">
          <div className="modal-content bundle-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('metaAgent.config.selectBundles')}</h2>
              {selectedBundleIds.length > 0 && (
                <span className="bundle-count-badge">
                  {t('metaAgent.config.selectedCount', { count: selectedBundleIds.length })}
                </span>
              )}
              <button
                type="button"
                className="btn-close"
                onClick={() => { setShowBundleSelector(false); setBundleSearchQuery(''); }}
                aria-label={t('common.close')}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="skill-search-container">
              <svg className="search-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input dir="auto"
                type="text"
                className="skill-search-input"
                placeholder={t('metaAgent.config.searchBundles')}
                value={bundleSearchQuery}
                onChange={(e) => setBundleSearchQuery(e.target.value)}
                autoFocus
              />
              {bundleSearchQuery && (
                <button
                  type="button"
                  className="clear-search-btn"
                  onClick={() => setBundleSearchQuery('')}
                  aria-label={t('common.close')}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            <div className="modal-body">
              {filteredBundles.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-text">{t('metaAgent.config.noBundles')}</div>
                </div>
              ) : (
                <div className="skills-list-vertical">
                  {filteredBundles.map((bundle) => {
                    const isSelected = selectedBundleIds.includes(bundle.data_bundle_id);
                    return (
                      <div
                        key={bundle.data_bundle_id}
                        className={`skill-list-item ${isSelected ? 'assigned' : ''}`}
                        onClick={() => toggleBundleSelection(bundle.data_bundle_id)}
                      >
                        <div className={`bundle-checkbox ${isSelected ? 'checked' : ''}`}>
                          {isSelected && (
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                        <div className="skill-item-icon">
                          <BundleIcon size={22} />
                        </div>
                        <div className="skill-item-content">
                          <div className="skill-item-name-row">
                            <span className="skill-item-name" dir="auto">{bundle.bundle_name}</span>
                            {bundle.is_shared && (
                              <span className="shared-badge">{t('common.shared')}</span>
                            )}
                          </div>
                          <div className="skill-item-description">
                            {bundle.bundle_description
                              || t('metaAgent.config.bundleFileCount', { count: bundle.file_count })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-primary"
                onClick={() => { setShowBundleSelector(false); setBundleSearchQuery(''); }}
              >
                {t('common.done')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Skill Selector Modal */}
      {showSkillSelector && (
        <div className="modal-overlay">
          <div className="modal-content skill-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('metaAgent.config.selectSkills')}</h2>
              <button
                type="button"
                className="btn-close"
                onClick={() => { setShowSkillSelector(false); setSkillSearchQuery(''); }}
                aria-label={t('common.close')}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="skill-search-container">
              <svg className="search-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input dir="auto"
                type="text"
                className="skill-search-input"
                placeholder={t('metaAgent.config.searchSkills')}
                value={skillSearchQuery}
                onChange={(e) => setSkillSearchQuery(e.target.value)}
                autoFocus
              />
              {skillSearchQuery && (
                <button
                  type="button"
                  className="clear-search-btn"
                  onClick={() => setSkillSearchQuery('')}
                  aria-label={t('common.close')}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            <div className="modal-body">
              {loadingSkills ? (
                <div className="loading-state">
                  <div className="loading-spinner" />
                  <p>{t('common.loading')}</p>
                </div>
              ) : filteredSkills.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-text">{t('metaAgent.config.noSkills')}</div>
                </div>
              ) : (
                <div className="skills-list-vertical">
                  {filteredSkills.map((skill) => {
                    const isAssigned = selectedSkillIds.includes(skill.skill_id);
                    return (
                      <div
                        key={skill.skill_id}
                        className={`skill-list-item ${isAssigned ? 'assigned' : ''}`}
                        onClick={() => toggleSkill(skill.skill_id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="skill-item-icon">
                          <SkillIcon size={24} />
                        </div>
                        <div className="skill-item-content">
                          <div className="skill-item-name-row">
                            <span className="skill-item-name" dir="auto">{skill.skill_name}</span>
                            {skill.is_shared && (
                              <span className="shared-badge">{t('common.shared')}</span>
                            )}
                          </div>
                          <div className="skill-item-description" dir="auto">{skill.skill_description}</div>
                        </div>
                        {isAssigned && (
                          <div className="skill-item-check">
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-primary"
                onClick={() => { setShowSkillSelector(false); setSkillSearchQuery(''); }}
              >
                {t('common.done')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AoAConfigModal;
